import { useEffect } from "react";
import { type MinimalQueryResult } from "@jsq/ds/types";
import { useResolveInvestmentAccountIdLazyQuery } from "~/graphql/supergraphHooks";
import { extractIdFromPathSegment } from "~/pages/Compliance/utils";
import { toDbId } from "~/utils/graphql-helpers";
import type { WizardCaseInfo } from "./types";

/**
 * Resolves the arena/entity ids and (optionally) the investment account id implied by the
 * case's prospect or position. Shared by the Profile and Review steps so both compare a
 * profile against the same resolved account.
 */
export const useCaseInvestmentAccount = (
  caseInfo: WizardCaseInfo | null,
): {
  arenaId: number | undefined;
  entityId: number | undefined;
  investmentAccountId: number | null;
  resolveState: MinimalQueryResult;
  resolveSettled: boolean;
} => {
  const arenaId = caseInfo?.clientId ? toDbId(caseInfo.clientId) : undefined;
  const entityId = caseInfo?.entityId ? toDbId(caseInfo.entityId) : undefined;
  const prospectId = caseInfo?.prospectIdOrSubscriptionUrl
    ? extractIdFromPathSegment(caseInfo.prospectIdOrSubscriptionUrl, "prospect")
    : null;
  const positionId = caseInfo?.positionId
    ? extractIdFromPathSegment(caseInfo.positionId, "position")
    : null;
  const hasResolveKey = prospectId !== null || positionId !== null;

  // cache-and-network so returning to a step serves the resolved account from cache instead of
  // flashing the page loader, while still refetching in the background for freshness.
  const [resolveInvestmentAccountId, resolveState] = useResolveInvestmentAccountIdLazyQuery({
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (arenaId === undefined || !hasResolveKey) return;
    void resolveInvestmentAccountId({
      variables: {
        arenaId,
        prospectId: prospectId ?? undefined,
        positionId: positionId?.toString(),
      },
    });
  }, [arenaId, hasResolveKey, prospectId, positionId, resolveInvestmentAccountId]);

  const investmentAccountId = resolveState.data?.resolveInvestmentAccountId ?? null;
  const resolveSettled = !hasResolveKey || (resolveState.called && !resolveState.loading);

  return { arenaId, entityId, investmentAccountId, resolveState, resolveSettled };
};
